import { get } from 'svelte/store';

import { PERFORMANCE_DURATION } from '../enums/track';
import {
    userID,
    isLogin,
    errorType,
    groupType,
    omegaParams,
    uiType
} from '../stores/index';

import {
    detectBrowser,
    detectNetworkInfo,
    detectOS,
    detectDeviceType
} from './index';

import {
    buildCreditCardCommonParams,
    getAdvParameters,
    getPageTrackingConfig
} from '@/utils/creditCardTrackParams';
import { getParamsUI, GROUP_TYPE_PARAM, UI_TYPES } from '@/utils/group';
import { trackEvent as trackEventFn } from '@/utils/omega';
import { isIOS } from '@/utils/os';
import url from '@/utils/url';

type UIType = 'A' | 'B' | 'C' | 'D' | 'E';

type TrackAttrs = {
    [key: string]:
        | string
        | boolean
        | number
        | object
        | []
        | undefined
        | null;
};

export const callErrorTrack = (type: string, errmsg?: string) => {
    trackEventFn('pub_marketing_call_error_bt', {
        type,
        errmsg,
        ext: {
            ...url.parseQuery()
        },
        ...getAdvParameters()
    });
};

export const trackEvent = (eventId: string, attrs?: TrackAttrs) => {
    try {
        const advParams = getAdvParameters();
        const publicParams = {
            // W2A 业务埋点统一走页面 pageId，广告公参与身份公参统一从 helper 获取。
            ...buildCreditCardCommonParams({
                uid: get(userID),
                pub_uid: get(userID),
                ext: {
                    ...advParams,
                    ui_type: getParamsUI(
                        GROUP_TYPE_PARAM,
                        UI_TYPES,
                        get(uiType) as UIType
                    ),
                    page_source: 'creditcard'
                }
            }),
            group_type: url.get('group_type') || get(groupType),
            from_traffic_cnn: url.get('from_traffic_cnn') || '',
            pub_from_quickbolt: url.get('pub_from_quickbolt') || '',
            relation_id: url.get('relation_id') || '',
            error_type: get(errorType),
            is_sign: get(isLogin) ? 1 : 0,
            page_type: get(groupType),
            ...getNewParams()
        };

        const commonParams = {
            ...publicParams,
            ...attrs
        };

        omegaParams.set(publicParams);

        return trackEventFn(eventId, commonParams);
    } catch (error) {
        console.error(error);
    }
};

interface PrivateAttrsForRisk {
    network_type?: string; // 新增网络类型
    network_status?: string; // 新增网络状态
    uid: string;
    pub_uid: string;
    timestamp?: number;
}

/**
 * 风控私参
 */
const privateAttrsForRisk = <PrivateAttrsForRisk>{
    browser_type: detectBrowser().browser_type,
    browser_version: detectBrowser().browser_version,
    language: navigator.language || 'es-MX',
    network_type: detectNetworkInfo().type, // 网络类型
    network_status: detectNetworkInfo().status, // 网络状态
    screen_width: window.screen.width, // 屏幕宽度
    screen_height: window.screen.height, // 屏幕高度
    screen_pixel:
        window.screen.width * window.devicePixelRatio +
        '*' +
        window.screen.height * window.devicePixelRatio, // 屏幕分辨率
    login_mobile: '',
    os: isIOS() ? 'ios' : 'android',
    os_version: detectOS().version,
    operating_system: isIOS() ? 'ios' : 'android',
    uid: get(userID),
    pub_uid: get(userID),
    pub_biz_line: 'fin',
    pub_subbiz: 'creditcard',
    country: 'MX'
};

const getNewParams = () => {
    const { pageId } = getPageTrackingConfig();

    return {
        page_name: pageId,
        network_status: detectNetworkInfo().status,
        os: detectOS().type,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        screen_pixel:
            window.screen.width * window.devicePixelRatio +
            '*' +
            window.screen.height * window.devicePixelRatio,
        device_type: detectDeviceType()
    };
};

/**
 * 该方法触发的埋点会在登录后批量派发
 * @note only for 风控
 */
export const trackWithCache = (key: string, attrs: TrackAttrs) => {
    const params = {
        ...privateAttrsForRisk,
        ...attrs
    };

    params.timestamp = Date.now();
    params.uid = get(userID);
    params.pub_uid = get(userID);
    trackEvent(key, params);
};

export { privateAttrsForRisk };

export const trackPerformance = (
    duration_name: string,
    duration_value: number,
    attrs?: TrackAttrs
) => {
    if (
        !duration_name ||
        !duration_value ||
        Number(duration_value) > 100000 ||
        Number(duration_value) < 0 ||
        isNaN(Number(duration_value))
    ) {
        return;
    }

    const params = {
        ...attrs,
        uid: get(userID),
        pub_uid: get(userID),
        duration_name,
        duration_value: Math.round(Number(duration_value)),
        pub_page: getPageTrackingConfig().pageId
    };

    trackEvent(PERFORMANCE_DURATION, params);
};
